import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao, Selo } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { Selecao } from '../../componentes/formulario';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro } from '../api';
import { rotuloAcao, rotuloModulo } from '../rotulos';
import { COR_NIVEL } from './dados';

interface ItemNivel {
  permissao: string;
  modulo: string;
  nivel_padrao: number;
  travado: boolean;
  nivel: number | null;
}

/**
 * Fase 2 (W11): Admin > Papéis, "Nível de risco para os assistentes". Para quem tem o papel, cada ação de escrita pode ser
 * reclassificada (N1 direto, N2 com confirmação, N3 nunca pelo assistente). O que é reservado à pessoa (papéis, chaves,
 * configurações, aprovar e publicar desenvolvimento) aparece travado em N3. O dono de cada assistente só endurece.
 */
export function NiveisPapel({ papelId, editavel }: { papelId: string; editavel: boolean }) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const chave = ['assistentes', 'niveis', papelId];
  const q = useQuery({ queryKey: chave, queryFn: () => get<{ itens: ItemNivel[] }>(`/assistentes/niveis/${papelId}`) });
  const [mudancas, setMudancas] = useState<Record<string, number | null>>({});
  const salvar = useMutation({
    mutationFn: () => api('PUT', `/assistentes/niveis/${papelId}`, { niveis: Object.entries(mudancas).map(([permissao, nivel]) => ({ permissao, nivel })) }),
    onSuccess: async () => {
      setMudancas({});
      toast.sucesso(t('comum.salvo'));
      await qc.invalidateQueries({ queryKey: chave });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const rotulo = (i: ItemNivel) => `${rotuloModulo(i.modulo)}: ${rotuloAcao(i.permissao.slice(i.permissao.indexOf('.') + 1))}`;
  return (
    <details className="rounded-lg border border-borda" data-niveis-papel>
      <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-tinta-1">{t('assistentes.papel.titulo')}</summary>
      <div className="border-t border-borda px-3 py-3">
        <p className="mb-3 text-xs text-tinta-3">{t('assistentes.papel.texto')}</p>
        <Estado consulta={q}>
          {(d) => (
            <>
              <ul className="flex flex-col divide-y divide-borda">
                {d.itens.map((i) => {
                  const v = i.permissao in mudancas ? mudancas[i.permissao]! : i.nivel;
                  const efetivo = i.travado ? 3 : (v ?? i.nivel_padrao);
                  return (
                    <li key={i.permissao} className="flex flex-col gap-2 py-2 sm:flex-row sm:items-center sm:justify-between">
                      <span className="flex min-w-0 items-center gap-2 text-sm text-tinta-1">
                        <Selo cor={COR_NIVEL[efetivo]}>N{efetivo}</Selo>
                        <span className="min-w-0 break-words">{rotulo(i)}</span>
                      </span>
                      {i.travado ? (
                        <span className="text-xs text-tinta-3 sm:w-72">{t('assistentes.papel.travado')}</span>
                      ) : (
                        <div className="sm:w-72">
                          <Selecao
                            aria-label={rotulo(i)}
                            disabled={!editavel}
                            value={v === null ? '' : String(v)}
                            onChange={(e) => setMudancas((m) => ({ ...m, [i.permissao]: e.target.value === '' ? null : Number(e.target.value) }))}
                            opcoes={[
                              { valor: '', rotulo: t('assistentes.papel.fabrica', { nivel: `N${i.nivel_padrao}` }) },
                              { valor: '1', rotulo: `N1: ${t('assistentes.niveis.n1')}` },
                              { valor: '2', rotulo: `N2: ${t('assistentes.niveis.n2')}` },
                              { valor: '3', rotulo: `N3: ${t('assistentes.niveis.n3')}` },
                            ]}
                          />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              {editavel && (
                <div className="mt-3 flex justify-end">
                  <Botao variante="secundario" disabled={!Object.keys(mudancas).length} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
                    {t('comum.salvar')}
                  </Botao>
                </div>
              )}
            </>
          )}
        </Estado>
      </div>
    </details>
  );
}
